import React from 'react';
import { useTranslation } from 'react-i18next';
import AddressAutocomplete from '../Shared/AddressAutocomplete';
import { AddressData } from '../CartDrawer';
import AddressMapPreview from './AddressMapPreview';

interface CheckoutAddressStepProps {
    addressData: AddressData;
    setAddressData: React.Dispatch<React.SetStateAction<AddressData>>;
    errors: Record<string, string>;
    isAuthenticated: boolean;
    email: string;
    setEmail: (email: string) => void;
    isDifferentAddress: boolean;
    onDifferentAddressToggle: (checked: boolean) => void;
    onSaveToProfile: () => Promise<void>;
    user: any;
    openAuthModal: (mode?: 'login' | 'register' | 'admin-login') => void;
}

const CheckoutAddressStep: React.FC<CheckoutAddressStepProps> = ({
    addressData,
    setAddressData,
    errors,
    isAuthenticated,
    email,
    setEmail,
    isDifferentAddress,
    onDifferentAddressToggle,
    onSaveToProfile,
    user,
    openAuthModal
}) => {
    const { t } = useTranslation('marketplace');
    const [isSaving, setIsSaving] = React.useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        await onSaveToProfile();
        setIsSaving(false);
    };

    return (
        <div className="space-y-4">
            {!isAuthenticated && (
                <div className="space-y-4">
                    <div className="bg-purple-50 p-4 rounded-2xl border border-purple-100 flex items-center justify-between animate-in slide-in-from-top-2 duration-300">
                        <div className="flex-1 mr-4">
                            <p className="text-sm font-bold text-purple-900">{t('checkout.guest_login_prompt')}</p>
                            <p className="text-[10px] text-purple-600 font-medium leading-tight">{t('checkout.guest_login_subtext')}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => openAuthModal('login')}
                            className="shrink-0 bg-purple-600 text-white text-[10px] font-black px-4 py-2 rounded-xl shadow-lg shadow-purple-200 active:scale-95 transition-all uppercase tracking-wider"
                        >
                            {t('common:actions.login')}
                        </button>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{t('checkout.email_label')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className={`w-full bg-gray-50 border-none rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-purple-500 ${errors.email ? 'ring-2 ring-red-500' : ''}`}
                            placeholder={t('checkout.email_placeholder')}
                        />
                        {errors.email && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.email}</p>}
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2 pb-2">
                <input
                    type="checkbox"
                    id="different-address"
                    checked={isDifferentAddress}
                    onChange={e => onDifferentAddressToggle(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="different-address" className="text-sm font-bold text-gray-700 cursor-pointer select-none">
                    {t('checkout.ship_to_different_address_label')}
                </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{t('checkout.first_name_label')}</label>
                    <input
                        type="text"
                        value={addressData.firstName}
                        onChange={e => setAddressData({ ...addressData, firstName: e.target.value })}
                        className={`w-full bg-gray-50 border-none rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-purple-500 ${errors.firstName ? 'ring-2 ring-red-500' : ''}`}
                        placeholder={t('checkout.first_name_placeholder')}
                    />
                    {errors.firstName && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.firstName}</p>}
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{t('checkout.last_name_label')}</label>
                    <input
                        type="text"
                        value={addressData.lastName}
                        onChange={e => setAddressData({ ...addressData, lastName: e.target.value })}
                        className={`w-full bg-gray-50 border-none rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-purple-500 ${errors.lastName ? 'ring-2 ring-red-500' : ''}`}
                        placeholder={t('checkout.last_name_placeholder')}
                    />
                    {errors.lastName && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.lastName}</p>}
                </div>
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{t('checkout.address_label')}</label>
                <AddressAutocomplete
                    value={addressData.address}
                    onChange={val => setAddressData(prev => ({ ...prev, address: val }))}
                    onSelect={data => setAddressData(prev => ({
                        ...prev,
                        address: data.address,
                        city: data.city,
                        zip: data.zip_code,
                        region: data.region,
                        country: data.country,
                        lat: data.lat,
                        lon: data.lon
                    }))}
                    defaultCountry={addressData.country}
                    isRelative={true}
                    className={`bg-gray-50 border-none p-3 text-sm font-bold ${errors.address ? 'ring-2 ring-red-500' : ''}`}
                />
                {errors.address && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.address}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{t('checkout.zip_label')}</label>
                    <input
                        type="text"
                        value={addressData.zip}
                        onChange={e => setAddressData({ ...addressData, zip: e.target.value })}
                        className={`w-full bg-gray-50 border-none rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-purple-500 ${errors.zip ? 'ring-2 ring-red-500' : ''}`}
                        placeholder={t('checkout.zip_placeholder')}
                    />
                    {errors.zip && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.zip}</p>}
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{t('checkout.city_label')}</label>
                    <input
                        type="text"
                        value={addressData.city}
                        onChange={e => setAddressData({ ...addressData, city: e.target.value })}
                        className={`w-full bg-gray-50 border-none rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-purple-500 ${errors.city ? 'ring-2 ring-red-500' : ''}`}
                        placeholder={t('checkout.city_placeholder')}
                    />
                    {errors.city && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.city}</p>}
                </div>
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{t('checkout.country_label')}</label>
                <input
                    type="text"
                    value={addressData.country}
                    onChange={e => setAddressData({ ...addressData, country: e.target.value })}
                    className="w-full bg-gray-50 border-none rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-purple-500"
                />
            </div>

            {/* Visual Confirmation using AddressMapPreview */}
            {addressData.lat && addressData.lon && (
                <div className="animate-in fade-in zoom-in-95 duration-300">
                    <AddressMapPreview
                        lat={addressData.lat}
                        lon={addressData.lon}
                        address={`${addressData.address}, ${addressData.zip} ${addressData.city}`}
                    />
                </div>
            )}

            {isAuthenticated && !isDifferentAddress && (
                (() => {
                    const isAddressModified =
                        addressData.address !== (user?.address || '') ||
                        addressData.zip !== (user?.zip_code || '') ||
                        addressData.city !== (user?.city || '') ||
                        addressData.firstName !== (user?.first_name || '') ||
                        addressData.lastName !== (user?.last_name || '') ||
                        addressData.country !== (user?.country || 'Belgique');

                    return isAddressModified && (
                        <div className="flex justify-start mt-4 pt-4 border-t border-gray-100 italic">
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={isSaving}
                                className="text-[11px] font-black text-purple-600 hover:text-purple-700 disabled:opacity-50 flex items-center gap-2 transition-all group"
                            >
                                <div className="w-5 h-5 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                                    {isSaving ? (
                                        <div className="w-2 h-2 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <span className="text-[12px]">💾</span>
                                    )}
                                </div>
                                {t('checkout.save_address_button')}
                            </button>
                        </div>
                    );
                })()
            )}
        </div>
    );
};

export default CheckoutAddressStep;
